import type { Proposal } from "@platform/mpx-quotes-grid";

export interface ProposalPlanIndexEntry {
  carrierId: string;
  /** The RFP-requested plan group this proposal plan answers. Falls back to
   * the proposal's own plan-group id for an off-script group with no
   * `rfpBenefitPlanGroupId`, mirroring the BFF's
   * `buildPlanGroupIdByProposalPlanId` (comps-table.ts). */
  planGroupId: string;
  /** "High" / "Medium" / "Low", per the SBS grid's own labelling
   * (Quotes.tsx's `planOptionEditorApi`). Empty for a single-option group or
   * an unset/`notApplicable` option — no suffix is appended in that case. */
  optionLabel: string;
}

const OPTION_LABEL: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

/**
 * Indexes every carrier proposal plan by id, for resolving a Quotes-scope
 * cell review's `benefitPlanId` to its carrier, requested plan group, and
 * (when the group is dual/tri-option) plan option label.
 */
export const buildProposalPlanIndex = (
  proposals: readonly Proposal[],
): Map<string, ProposalPlanIndexEntry> => {
  const index = new Map<string, ProposalPlanIndexEntry>();

  for (const proposal of proposals) {
    for (const coverage of proposal.coverages) {
      for (const group of coverage.planGroups) {
        const planGroupId = group.rfpBenefitPlanGroupId ?? group.id;
        const isMultiOption = group.plans.length >= 2;

        for (const plan of group.plans) {
          index.set(plan.id, {
            carrierId: proposal.carrierId,
            planGroupId,
            optionLabel: isMultiOption
              ? (OPTION_LABEL[plan.optionName ?? ""] ?? "")
              : "",
          });
        }
      }
    }
  }

  return index;
};
